import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, CheckCircle, Clock, Wrench, ShieldAlert, TrendingDown, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ETIKET_CONFIG = {
  kirmizi: { bg: 'bg-red-500', text: 'text-white', label: 'Kırmızı', border: 'border-red-500' },
  sari: { bg: 'bg-yellow-500', text: 'text-black', label: 'Sarı', border: 'border-yellow-500' },
  mavi: { bg: 'bg-blue-500', text: 'text-white', label: 'Mavi', border: 'border-blue-500' },
  yesil: { bg: 'bg-green-500', text: 'text-white', label: 'Yeşil', border: 'border-green-500' }
};

const BINA_RENK = {
  kirmizi: 'border-l-red-500 bg-red-950/20',
  sari: 'border-l-yellow-500 bg-yellow-950/20',
  yesil: 'border-l-green-500 bg-green-950/20'
};

export default function PKDashboard() {
  const [data, setData] = useState(null);
  const [selectedBina, setSelectedBina] = useState(null);
  const [binaEkipmanlar, setBinaEkipmanlar] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/pk/dashboard`, { withCredentials: true });
      setData(res.data);
      if (res.data.binalar?.length > 0) {
        handleBinaSelect(res.data.binalar[0]);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBinaSelect = async (bina) => {
    setSelectedBina(bina);
    try {
      const res = await axios.get(`${API}/pk/binalar/${bina.id}/ekipmanlar`, { withCredentials: true });
      setBinaEkipmanlar(res.data);
    } catch (err) {
      console.error('Ekipman fetch error:', err);
    }
  };

  const getGeriSayim = (ekipman) => {
    if (!ekipman.etiket_timestamp_t0) return null;
    const t0 = new Date(ekipman.etiket_timestamp_t0);
    const now = new Date();
    const gunGecen = Math.floor((now - t0) / (1000 * 60 * 60 * 24));
    const toplam = ekipman.etiket_rengi === 'kirmizi' ? 60 : 120;
    const kalan = toplam - gunGecen;
    return { gunGecen, toplam, kalan, yuzde: Math.max(0, Math.min(100, (gunGecen / toplam) * 100)) };
  };

  const getSonrakiKontrolKalan = (ekipman) => {
    if (!ekipman.sonraki_kontrol_tarihi) return null;
    const sonraki = new Date(ekipman.sonraki_kontrol_tarihi);
    const now = new Date();
    return Math.ceil((sonraki - now) / (1000 * 60 * 60 * 24));
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Yükleniyor...</div>;
  if (!data) return <div className="text-red-400 text-center py-8">Veriler yüklenemedi</div>;

  const kpiCards = [
    { label: 'Toplam Ekipman', value: data.toplam_ekipman, icon: Wrench, color: 'text-blue-400', bg: 'bg-blue-900/30' },
    { label: 'Kontrol Yaklaşan', value: data.kontrol_yaklasan, icon: Clock, color: 'text-orange-400', bg: 'bg-orange-900/30' },
    { label: 'Kontrol Geçmiş', value: data.kontrol_gecmis, icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-900/30' },
    { label: 'Açık Arıza', value: data.acik_ariza, icon: ShieldAlert, color: 'text-yellow-400', bg: 'bg-yellow-900/30' },
    { label: 'SLA İhlali', value: data.sla_ihlal, icon: TrendingDown, color: 'text-red-500', bg: 'bg-red-900/40' },
    { label: 'Aylık Ceza', value: `${(data.toplam_ceza || 0).toLocaleString('tr-TR')} TL`, icon: TrendingDown, color: 'text-purple-400', bg: 'bg-purple-900/30' }
  ];

  return (
    <div className="space-y-6" data-testid="pk-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpiCards.map((kpi, i) => (
          <Card key={i} className={`${kpi.bg} border-gray-700/50`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                {kpi.label === 'SLA İhlali' && data.sla_ihlal > 0 && (
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                )}
              </div>
              <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
              <p className="text-xs text-gray-400 mt-1">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bina + Ekipman Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sol: Bina Listesi */}
        <Card className="bg-gray-900/80 border-gray-700/50 lg:col-span-1">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Binalar ({data.binalar.length})
            </h3>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {data.binalar.map(bina => (
                <div
                  key={bina.id}
                  onClick={() => handleBinaSelect(bina)}
                  className={`p-3 rounded-lg border-l-4 cursor-pointer transition-all ${BINA_RENK[bina.renk] || 'border-l-gray-600'} ${selectedBina?.id === bina.id ? 'ring-1 ring-blue-500' : 'hover:bg-gray-800/50'}`}
                  data-testid={`bina-card-${bina.id}`}
                >
                  <p className="text-sm font-medium text-white">{bina.bina_adi}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-400">{bina.ilce}</span>
                    <span className="text-xs text-gray-500">|</span>
                    <span className="text-xs text-gray-400">{bina.ekipman_sayisi} ekipman</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sağ: Ekipman Listesi */}
        <Card className="bg-gray-900/80 border-gray-700/50 lg:col-span-2">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              {selectedBina ? `${selectedBina.bina_adi} - Ekipmanlar (${binaEkipmanlar.length})` : 'Bir bina seçin'}
            </h3>
            {binaEkipmanlar.length === 0 ? (
              <p className="text-gray-500 text-sm py-8 text-center">Bu binada ekipman yok</p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {binaEkipmanlar.map(ek => {
                  const gs = getGeriSayim(ek);
                  const kontrol_kalan = getSonrakiKontrolKalan(ek);
                  const etiketCfg = ETIKET_CONFIG[ek.etiket_rengi];

                  return (
                    <div key={ek.id} className={`p-3 rounded-lg border ${ek.etiket_rengi === 'kirmizi' ? 'border-red-700/60 bg-red-950/20' : ek.etiket_rengi === 'sari' ? 'border-yellow-700/40 bg-yellow-950/10' : 'border-gray-700/40 bg-gray-800/30'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="text-sm font-medium text-white">{ek.alt_tip || ek.ekipman_tipi}</p>
                            <p className="text-xs text-gray-400">{ek.marka} {ek.model} — {ek.seri_no}</p>
                            {ek.asansor_kimlik_no && <p className="text-xs text-blue-400">Kimlik: {ek.asansor_kimlik_no}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {etiketCfg && (
                            <Badge className={`${etiketCfg.bg} ${etiketCfg.text} text-xs`}>{etiketCfg.label}</Badge>
                          )}
                          {kontrol_kalan !== null && (
                            <Badge className={`text-xs ${kontrol_kalan <= 0 ? 'bg-red-600 text-white' : kontrol_kalan <= 30 ? 'bg-orange-600 text-white' : 'bg-gray-700 text-gray-300'}`}>
                              {kontrol_kalan <= 0 ? `${Math.abs(kontrol_kalan)} gün geçmiş` : `${kontrol_kalan} gün`}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Geri Sayım (Kırmızı/Sarı etiket) */}
                      {gs && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className={ek.etiket_rengi === 'kirmizi' ? 'text-red-400' : 'text-yellow-400'}>
                              {gs.kalan > 0 ? `${gs.toplam} günden ${gs.kalan} gün kaldı` : 'Süre doldu!'}
                            </span>
                            <span className="text-gray-500">{gs.gunGecen}/{gs.toplam} gün</span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${ek.etiket_rengi === 'kirmizi' ? 'bg-red-500' : 'bg-yellow-500'}`}
                              style={{ width: `${Math.min(gs.yuzde, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Asansör Durum Tablosu */}
      <Card className="bg-gray-900/80 border-gray-700/50">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Asansör Durum Tablosu</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-xs text-gray-400">
                  <th className="text-left py-2 px-3">Bina</th>
                  <th className="text-left py-2 px-3">Kimlik No</th>
                  <th className="text-center py-2 px-3">Etiket</th>
                  <th className="text-center py-2 px-3">Kalan Gün</th>
                  <th className="text-left py-2 px-3">Bakım Firması</th>
                  <th className="text-left py-2 px-3">Son Kontrol</th>
                  <th className="text-left py-2 px-3">Durum</th>
                </tr>
              </thead>
              <tbody>
                {binaEkipmanlar.length === 0 && data.binalar.length > 0 ? (
                  <tr><td colSpan={7} className="text-center py-4 text-gray-500">Bina seçin veya asansör yok</td></tr>
                ) : (
                  binaEkipmanlar
                    .filter(ek => ek.ekipman_tipi === 'asansor')
                    .sort((a, b) => {
                      const order = { kirmizi: 0, sari: 1, mavi: 2, yesil: 3 };
                      return (order[a.etiket_rengi] ?? 4) - (order[b.etiket_rengi] ?? 4);
                    })
                    .map(ek => {
                      const gs = getGeriSayim(ek);
                      const etiketCfg = ETIKET_CONFIG[ek.etiket_rengi];
                      return (
                        <tr key={ek.id} className={`border-b border-gray-800/50 ${ek.etiket_rengi === 'kirmizi' ? 'bg-red-950/30' : ek.etiket_rengi === 'sari' ? 'bg-yellow-950/20' : ''}`}>
                          <td className="py-2 px-3 text-white">{selectedBina?.bina_adi}</td>
                          <td className="py-2 px-3 text-blue-300 font-mono text-xs">{ek.asansor_kimlik_no}</td>
                          <td className="py-2 px-3 text-center">
                            {etiketCfg && <Badge className={`${etiketCfg.bg} ${etiketCfg.text} text-xs`}>{etiketCfg.label}</Badge>}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {gs ? (
                              <span className={gs.kalan <= 5 ? 'text-red-400 font-bold' : gs.kalan <= 15 ? 'text-orange-400' : 'text-gray-300'}>
                                {gs.kalan > 0 ? gs.kalan : 'Doldu!'}
                              </span>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-gray-300 text-xs">{ek.bakim_yuklenici_firma_id ? 'Anadolu Asansör' : '—'}</td>
                          <td className="py-2 px-3 text-gray-400 text-xs">{ek.son_kontrol_tarihi || '—'}</td>
                          <td className="py-2 px-3">
                            <Badge className={`text-xs ${ek.durum === 'guvenisiz' ? 'bg-red-600' : ek.durum === 'kusurlu' ? 'bg-yellow-600 text-black' : ek.durum === 'kusursuz' ? 'bg-green-600' : 'bg-gray-600'}`}>
                              {ek.durum}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
